import React from 'react';
import {
  Box,
  Button,
  Field,
  Heading,
  Icon,
  IconButton,
  Input,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { resetPassword, logger } from '@kuow/core';

const PASSWORD_MIN = 8;

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token') ?? searchParams.get('recovery_token') ?? '';
  const recoveryToken = React.useMemo(() => {
    try {
      return decodeURIComponent(rawToken).trim();
    } catch {
      return '';
    }
  }, [rawToken]);
  const isTokenMissing = recoveryToken.length === 0;

  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{ password?: string }>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;

    setFormError(null);
    const nextErrors: { password?: string } = {};
    if (password.length < PASSWORD_MIN) {
      nextErrors.password = `Password must be at least ${PASSWORD_MIN} characters`;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    void (async () => {
      try {
        await resetPassword({ recovery_token: recoveryToken, new_password: password });
        setSuccess(true);
        setPassword('');
      } catch (error) {
        logger.error('Password reset failed', { error });
        setFormError(
          'Unable to reset password. The link may have expired — please request a new one.',
        );
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | KUOW</title>
      </Helmet>
      <Box w="full" py={{ base: 10, md: 16 }} display="flex" flex="1" alignItems="center">
        <Box w="full" maxW={{ base: 'full', md: '450px' }} mx="auto" px={{ base: 4, md: 6 }}>
          <Box
            bg="bg"
            borderRadius="l3"
            boxShadow={{ base: 'none', md: 'lg' }}
            p={{ base: 6, md: 6 }}
          >
            <Box asChild>
              <form onSubmit={handleSubmit} noValidate>
                <VStack gap={4} align="stretch">
                  <Heading fontSize="2xl" fontWeight="semibold" lineHeight="32px">
                    Create a new password
                  </Heading>

                  {isTokenMissing ? (
                    <>
                      <Box
                        bg="red.subtle"
                        border="1px solid"
                        borderColor="red.emphasized"
                        color="red.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm" textAlign="center">
                          This reset link is missing or invalid. Please request a new one.
                        </Text>
                      </Box>
                      <Box display="flex" justifyContent="center">
                        <Link asChild color="river.solid" textDecoration="underline" fontSize="sm">
                          <RouterLink to="/sign-in">Back to Sign In</RouterLink>
                        </Link>
                      </Box>
                    </>
                  ) : success ? (
                    <>
                      <Box
                        bg="green.subtle"
                        border="1px solid"
                        borderColor="green.emphasized"
                        color="green.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm" textAlign="center">
                          Password updated successfully. You can now sign in with your new password.
                        </Text>
                      </Box>
                      <Box display="flex" justifyContent="center">
                        <Button asChild variant="solid" colorPalette="river">
                          <RouterLink to="/sign-in">Sign In</RouterLink>
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Text fontSize="xs" lineHeight="16px">
                        Enter a new password for your KUOW account. Must be at least{' '}
                        {PASSWORD_MIN} characters.
                      </Text>

                      <Stack gap={3.5} align="stretch">
                        <Field.Root invalid={Boolean(errors.password)} required>
                          <Field.Label>
                            New Password
                            <Field.RequiredIndicator />
                          </Field.Label>
                          <Box position="relative" w="full">
                            <Input
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              minLength={PASSWORD_MIN}
                              value={password}
                              onChange={(event) => setPassword(event.target.value)}
                              pr="10"
                              disabled={loading}
                            />
                            <IconButton
                              variant="ghost"
                              size="sm"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              onClick={() => setShowPassword((prev) => !prev)}
                              position="absolute"
                              right="1"
                              top="50%"
                              transform="translateY(-50%)"
                              disabled={loading}
                              zIndex={1}
                            >
                              <Icon as={showPassword ? LuEyeOff : LuEye} />
                            </IconButton>
                          </Box>
                          {errors.password ? (
                            <Field.ErrorText>{errors.password}</Field.ErrorText>
                          ) : null}
                        </Field.Root>

                        {formError ? (
                          <Box
                            bg="red.subtle"
                            border="1px solid"
                            borderColor="red.emphasized"
                            color="red.fg"
                            borderRadius="l2"
                            px="4"
                            py="3"
                          >
                            <Text fontSize="sm" textAlign="center">
                              {formError}
                            </Text>
                          </Box>
                        ) : null}

                        <Box display="flex" justifyContent="center">
                          <Button
                            type="submit"
                            variant="solid"
                            colorPalette="river"
                            loading={loading}
                          >
                            Update Password
                          </Button>
                        </Box>
                      </Stack>

                      <Text fontSize="xs" textAlign="center">
                        <Link asChild color="river.solid" textDecoration="underline">
                          <RouterLink to="/sign-in">Back to Sign In</RouterLink>
                        </Link>
                      </Text>
                    </>
                  )}
                </VStack>
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
